import React, { useState, useRef, useEffect } from 'react'
import { FaPaperPlane, FaRobot, FaUser } from 'react-icons/fa'
import './AI.css'
import '../../components/grids/Grids.css'
import { starPNG } from '../../assets'

interface Message {
  id: string
  content: string
  sender: 'user' | 'ai'
  timestamp: Date
}

const AI = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      content: 'Hello! I\'m Zeena, your AI assistant. How can I help you today?',
      sender: 'ai',
      timestamp: new Date()
    }
  ])
  const [inputMessage, setInputMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Auto-scroll to bottom when new messages are added
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return

    const userMessage: Message = {
      id: Date.now().toString(),
      content: inputMessage.trim(),
      sender: 'user',
      timestamp: new Date()
    }

    setMessages(prev => [...prev, userMessage])
    setInputMessage('')
    setIsLoading(true)

    // Simulate AI response (replace this with actual AI API call)
    setTimeout(() => {
      const aiResponse: Message = {
        id: (Date.now() + 1).toString(),
        content: generateAIResponse(userMessage.content),
        sender: 'ai',
        timestamp: new Date()
      }
      setMessages(prev => [...prev, aiResponse])
      setIsLoading(false)
    }, 1000 + Math.random() * 2000) // Random delay between 1-3 seconds
  }

  // Simple AI response generator (replace with actual AI integration)
  const generateAIResponse = (userInput: string): string => {
    const responses = [
      "That's an interesting question! Let me help you with that.",
      "I understand what you're asking. Here's what I think...",
      "Based on your question, I can provide some insights.",
      "Great question! Let me break this down for you.",
      "I'd be happy to help you with that. Here's my response...",
      "Thanks for asking! Here's what I can tell you about that topic.",
      "That's a thoughtful question. Let me provide some guidance.",
      "I can definitely help you with that. Here's my analysis..."
    ]
    
    // Simple keyword-based responses
    const lowerInput = userInput.toLowerCase()
    
    if (lowerInput.includes('hello') || lowerInput.includes('hi')) {
      return "Hello there! It's great to meet you. What would you like to know or discuss today?"
    }
    
    if (lowerInput.includes('help')) {
      return "I'm here to help! You can ask me questions about business processes, data analysis, project management, or anything else you'd like assistance with."
    }
    
    if (lowerInput.includes('thank')) {
      return "You're very welcome! I'm always happy to help. Is there anything else you'd like to discuss?"
    }
    
    // Default response with some context
    const randomResponse = responses[Math.floor(Math.random() * responses.length)]
    return `${randomResponse} Regarding "${userInput}", I think this is a valuable topic that deserves careful consideration. While I'm still learning about your specific business context, I can offer general guidance and help you think through the various aspects of this question.`
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const formatTime = (timestamp: Date): string => {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  return (
    <div className="ai-chat-container">
      <div className="grid__title__container">
        <div className="grid__header__background">
          <div className="grid__title">
            <div style={{ color: "white", padding: "1rem 0 1rem 0" }}>
              <div>
                Zeena
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="ai-chat-messages">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`message ${message.sender === 'user' ? 'message-user' : 'message-ai'}`}
          >
            <div className="message-avatar">
              {message.sender === 'user' ? <FaUser /> : <FaRobot />}
            </div>
            <div className="message-content">
              <div className="message-text">{message.content}</div>
              <div className="message-time">{formatTime(message.timestamp)}</div>
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="message message-ai">
            <div className="message-avatar">
              <FaRobot />
            </div>
            <div className="message-content">
              <div className="message-text typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      <div className="ai-chat-input">
        <div className="input-container">
          <textarea
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="Type your message here..."
            rows={1}
            disabled={isLoading}
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isLoading}
            className="send-button"
          >
            <FaPaperPlane />
          </button>
        </div>
      </div>
    </div>
  )
}

export default AI